import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, Phone, Download, CalendarCheck, MessageCircle } from 'lucide-react';
import { BRAND, CONTACT, JOURNEY } from '@/content';
import { track } from '@/lib/track';
import { scrollToTarget } from '@/lib/scrollTo';

gsap.registerPlugin(ScrollTrigger);

const JourneyScene = lazy(() => import('@/components/JourneyScene'));

const SCENES = [
  { key: 'c0', a: 0.0, b: 0.08 },
  { key: 'c1', a: 0.06, b: 0.22 },
  { key: 'c2', a: 0.22, b: 0.37 },
  { key: 'c3', a: 0.37, b: 0.52 },
  { key: 'c4', a: 0.52, b: 0.65 },
  { key: 'c5', a: 0.65, b: 0.78 },
  { key: 'c6', a: 0.78, b: 0.9 },
  { key: 'c7', a: 0.9, b: 1.0 },
];

const CHAPTERS = [
  { label: 'Arrival', a: 0, b: 0.08 },
  { label: 'Ready', a: 0.08, b: 0.37 },
  { label: 'Architecture', a: 0.37, b: 0.52 },
  { label: 'Homes', a: 0.52, b: 0.78 },
  { label: 'Connected', a: 0.78, b: 0.9 },
  { label: 'Visit', a: 0.9, b: 1 },
];

const PRELOAD = [
  '/assets/hero-elevation.webp',
  '/assets/elevation-day.webp',
  '/assets/room-living.webp',
  '/assets/room-kitchen.webp',
  '/assets/room-bedroom.webp',
  '/assets/balcony-xl.webp',
  '/assets/map-connectivity.webp',
  BRAND.logo,
];

const clamp01 = (t) => Math.min(1, Math.max(0, t));
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

function Line({ t, d = 0.16, className = '', children }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <span data-reveal data-t={t} data-d={d} className="block will-change-transform" style={{ opacity: 0, transform: 'translateY(110%)' }}>
        {children}
      </span>
    </span>
  );
}

// Static, fully accessible version for prefers-reduced-motion
function StaticJourney({ onEnquire }) {
  return (
    <section id="overview" data-testid="journey-static" className="bg-[#14151A] text-white">
      <div className="mx-auto flex min-h-[100dvh] max-w-7xl flex-col items-center justify-center px-6 py-24 text-center">
        <img src={BRAND.logo} alt="Codename Metro One — Navade" className="h-14 w-auto rounded-lg bg-white p-2" />
        <h1 className="mt-8 font-display text-4xl font-extrabold uppercase tracking-tight sm:text-6xl">
          LIFE IN A <span className="text-[#C92F4B]">METRO ONE</span>
        </h1>
        <p className="mt-3 font-display text-sm font-bold uppercase tracking-[0.24em] text-white/70">Ready Today. Connected to Tomorrow.</p>
        <p className="mt-5 max-w-xl text-base text-white/70">{JOURNEY.proposition}</p>
        <ul className="mt-6 flex flex-wrap justify-center gap-2 text-xs font-semibold text-white/80">
          {['Ready-to-Move Homes', 'OC Received', '1 & 2 BHK Ventilated Homes', 'Semi & Fully Furnished Options'].map((t) => (
            <li key={t} className="rounded-full border border-white/20 px-4 py-2">{t}</li>
          ))}
        </ul>
        <img src="/assets/elevation-day.webp" alt="Official elevation of Aarohi Complex, Navade" className="mt-10 w-full max-w-3xl rounded-2xl" loading="lazy" />
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button data-testid="journey-static-visit-btn" onClick={onEnquire} className="btn-primary">Book Your Private Site Visit</button>
          <a href={`tel:${CONTACT.phone}`} className="btn-ghost !border-white/30 !bg-transparent !text-white">Call {CONTACT.phoneDisplay}</a>
        </div>
      </div>
    </section>
  );
}

export default function ImmersiveJourney({ onEnquire }) {
  const outer = useRef(null);
  const scene = useRef(null);
  const R = useRef({}).current;
  const jm = useRef({ invalidate: null });
  const progressRef = useRef(0);
  const reveals = useRef({});
  const [chapter, setChapter] = useState(0);
  const [showSkip, setShowSkip] = useState(false);
  const [loadPct, setLoadPct] = useState(0);
  const [ready, setReady] = useState(false);
  const [glbOk, setGlbOk] = useState(false);      // file exists + WebGL supported
  const [glbActive, setGlbActive] = useState(false); // canvas mounted (on scroll intent / idle)
  const [glbLive, setGlbLive] = useState(false);     // first 3D frame painted → crossfade from 2.5D
  const [reduced, setReduced] = useState(false);
  const [mob, setMob] = useState(false);

  // environment detection + asset preload + optional GLB probe
  useEffect(() => {
    const red = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setReduced(red);
    setMob(window.innerWidth < 768);
    if (red) return;

    let done = 0;
    const critical = ['/assets/silhouette.webp', BRAND.logo];
    const isMob = window.innerWidth < 768;
    const deferred = PRELOAD.filter((s) => !critical.includes(s)).map((s) => (isMob && s.startsWith('/assets/room-') ? s.replace('.webp', '-m.webp') : s));
    let finished = false;
    const markDone = () => {
      if (finished) return;
      finished = true;
      setReady(true);
      ScrollTrigger.refresh();
      // non-critical scene images wait for real scroll intent (or a long idle timeout)
      const loadRest = () => deferred.forEach((src) => { const im = new Image(); im.src = src; });
      window.addEventListener('scroll', loadRest, { once: true, passive: true });
      setTimeout(loadRest, 12000);
    };
    critical.forEach((src) => {
      const im = new Image();
      im.onload = im.onerror = () => {
        done += 1;
        setLoadPct(Math.round((done / critical.length) * 100));
        if (done === critical.length) markDone();
      };
      im.src = src;
    });

    const webgl = (() => { try { return !!document.createElement('canvas').getContext('webgl'); } catch { return false; } })();
    if (webgl) {
      fetch(BRAND.model3d, { method: 'HEAD' })
        .then((r) => {
          const type = r.headers.get('content-type') || '';
          if (r.ok && !type.includes('text/html')) setGlbOk(true);
        })
        .catch(() => {});
    }
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener('load', onLoad);
    if (document.fonts?.ready) document.fonts.ready.then(() => ScrollTrigger.refresh());
    return () => window.removeEventListener('load', onLoad);
  }, []);

  // 3D activates on first scroll intent — 2.5D renders instantly first
  useEffect(() => {
    if (!glbOk || reduced || glbActive) return;
    const activate = () => {
      setGlbActive(true);
      // deferred journey images (data-src) load only on scroll intent
      document.querySelectorAll('.jdefer').forEach((el) => { if (el.dataset.src) el.src = el.dataset.src; });
      document.querySelectorAll('.jdefer-src').forEach((el) => { if (el.dataset.srcset) el.srcset = el.dataset.srcset; });
    };
    window.addEventListener('scroll', activate, { once: true, passive: true });
    window.addEventListener('pointerdown', activate, { once: true, passive: true });
    return () => {
      window.removeEventListener('scroll', activate);
      window.removeEventListener('pointerdown', activate);
    };
  }, [glbOk, reduced, glbActive]);

  // master scroll timeline
  useEffect(() => {
    if (reduced) return;

    // cache reveal nodes per scene
    SCENES.forEach(({ key }) => {
      const cont = R[key];
      if (cont) reveals.current[key] = [...cont.querySelectorAll('[data-reveal]')];
    });

    const update = (p) => {
      progressRef.current = p;
      jm.current.invalidate?.();
      // progress bar
      if (R.pbar) R.pbar.style.transform = `scaleX(${p})`;
      // chapter state (react — changes rarely)
      let ci = CHAPTERS.findIndex((c) => p < c.b);
      if (ci === -1) ci = CHAPTERS.length - 1;
      setChapter((prev) => (prev === ci ? prev : ci));
      setShowSkip((prev) => {
        const v = p > 0.03 && p < 0.96;
        return prev === v ? prev : v;
      });
      CHAPTERS.forEach((c, i) => {
        const f = R[`chf${i}`];
        if (f) f.style.transform = `scaleX(${clamp01((p - c.a) / (c.b - c.a))})`;
      });

      // hide WebGL canvas behind the interior/balcony/map scenes
      if (R.canvas) R.canvas.style.visibility = p > 0.5 && p < 0.9 ? 'hidden' : 'visible';

      SCENES.forEach(({ key, a, b }, i) => {
        const cont = R[key];
        if (!cont) return;
        const local = clamp01((p - a) / (b - a));
        const fin = i === 0 ? 1 : clamp01((p - (a - 0.02)) / 0.03);
        const fout = i === SCENES.length - 1 ? 1 : clamp01((b + 0.02 - p) / 0.03);
        const op = Math.min(fin, fout);
        cont.style.opacity = op;
        cont.style.visibility = op <= 0.001 ? 'hidden' : 'visible';
        // reveal lines
        (reveals.current[key] || []).forEach((el) => {
          const t0 = parseFloat(el.dataset.t || '0');
          const dd = parseFloat(el.dataset.d || '0.16');
          const v = easeOut(clamp01((local - t0) / dd));
          el.style.opacity = v;
          if (el.dataset.word !== undefined) {
            const tOut = parseFloat(el.dataset.o || '99');
            const vOut = 1 - easeOut(clamp01((local - tOut) / 0.09));
            el.style.opacity = v * vOut;
            el.style.transform = `scale(${0.96 + 0.04 * v})`;
            el.style.filter = `blur(${(1 - v) * 8}px)`;
          } else {
            el.style.transform = `translateY(${(1 - v) * 110}%)`;
          }
        });

        // per-scene cinematic transforms (2.5D fallback layers; dormant when GLB active)
        if (key === 'c0' && R.s0img) {
          R.s0img.style.opacity = 0.25 + local * 0.45;
        }
        if (key === 'c1' && R.s1img) {
          const sc = mob ? 0.68 + local * 0.28 : 0.66 + local * 0.3;
          R.s1img.style.transform = `translateY(${(1 - local) * (mob ? 12 : 8)}%) scale(${sc})`;
          R.s1img.style.filter = `brightness(${0.28 + local * 0.5})`;
          if (R.s1fog) R.s1fog.style.opacity = 0.78 - local * 0.52;
          if (R.s1trail1) R.s1trail1.style.transform = `translateX(${-50 + local * 170}%)`;
          if (R.s1trail2) R.s1trail2.style.transform = `translateX(${60 - local * 170}%)`;
        }
        if (key === 'c2' && R.s2img) {
          R.s2img.style.transform = `translateY(${(13 - local * 5).toFixed(2)}%) scale(${(1.32 - local * 0.14).toFixed(3)})`;
          R.s2img.style.filter = `brightness(${(0.35 + local * 0.65).toFixed(2)})`;
          if (R.s2night) R.s2night.style.opacity = 0.88 - local * 0.72;
          if (R.s2blue) R.s2blue.style.opacity = 0.55 - local * 0.45;
        }
        if (key === 'c3') {
          if (R.s3imgA) {
            R.s3imgA.style.opacity = clamp01(1.1 - local * 1.25);
            R.s3imgA.style.transform = `translateX(${-local * 6}%) scale(${1.16 - local * 0.04})`;
          }
          if (R.s3imgB) {
            R.s3imgB.style.opacity = clamp01(local * 1.4 - 0.2);
            R.s3imgB.style.transform = `translateX(${(1 - local) * 6}%) scale(${1.08 + local * 0.04})`;
          }
        }
        if (key === 'c4') {
          if (R.s4ring) {
            const sc = local < 0.45 ? 0.25 + (local / 0.45) * 34 : 34.25;
            R.s4ring.style.transform = `translate(-50%,-50%) scale(${sc.toFixed(2)})`;
            R.s4ring.style.opacity = local < 0.5 ? 1 : clamp01(1 - (local - 0.5) / 0.12);
          }
          if (R.s4wrap) R.s4wrap.style.opacity = clamp01((local - 0.38) / 0.1);
          const wins = [[0.4, 0.64], [0.56, 0.8], [0.72, 1.0]];
          wins.forEach(([w0, w1], wi) => {
            const el = R[`s4i${wi}`];
            if (!el) return;
            const lin = clamp01((local - w0) / 0.08);
            const lout = wi === 2 ? 1 : clamp01((w1 - local) / 0.08);
            el.style.opacity = Math.min(lin, lout);
            el.style.transform = `scale(${1.07 - clamp01((local - w0) / (w1 - w0)) * 0.07})`;
          });
        }
        if (key === 'c5') {
          if (R.s5l0) R.s5l0.style.transform = `translateY(${local * -9}%) scale(1.22)`;
          if (R.s5l1) R.s5l1.style.transform = `translateY(${local * -4.5}%) scale(1.1)`;
          if (R.s5light) R.s5light.style.opacity = 0.12 + 0.14 * Math.sin(local * Math.PI);
        }
        if (key === 'c6') {
          if (R.s6map) {
            R.s6map.style.transform = `translateY(${(8 - local * 8).toFixed(2)}%) scale(${(1.22 - local * 0.22).toFixed(3)})`;
            R.s6map.style.filter = `brightness(${(0.82 + local * 0.18).toFixed(2)})`;
          }
          if (R.s6dots) R.s6dots.style.opacity = clamp01((local - 0.35) / 0.2);
        }
        if (key === 'c7' && R.s7img) {
          R.s7img.style.transform = `translateY(${((1 - local) * 7).toFixed(2)}%) scale(${(1.14 - local * 0.14).toFixed(3)})`;
          R.s7img.style.filter = `brightness(${(0.95 + local * 0.07).toFixed(2)})`;
          if (R.s7cta) R.s7cta.style.pointerEvents = p > 0.92 ? 'auto' : 'none';
        }
      });
    };

    const st = ScrollTrigger.create({
      trigger: outer.current,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.5,
      onUpdate: (self) => update(self.progress),
    });
    update(progressRef.current);
    return () => st.kill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced, mob, glbOk]);

  // cursor ring (desktop only)
  useEffect(() => {
    if (reduced || !window.matchMedia('(pointer:fine)').matches) return;
    const ring = R.cursor;
    const sc = scene.current;
    if (!ring || !sc) return;
    let tx = -100, ty = -100, x = -100, y = -100, raf;
    const move = (e) => {
      const r = sc.getBoundingClientRect();
      tx = e.clientX - r.left;
      ty = e.clientY - r.top;
    };
    const loop = () => {
      x += (tx - x) * 0.16;
      y += (ty - y) * 0.16;
      ring.style.transform = `translate(${x - 18}px, ${y - 18}px)`;
      raf = requestAnimationFrame(loop);
    };
    sc.addEventListener('pointermove', move);
    raf = requestAnimationFrame(loop);
    return () => {
      sc.removeEventListener('pointermove', move);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced]);

  // magnetic buttons (desktop)
  const magnet = (e) => {
    if (!window.matchMedia('(pointer:fine)').matches) return;
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    if (e.type === 'pointerleave') {
      el.style.transform = 'translate(0px,0px)';
      return;
    }
    el.style.transform = `translate(${((e.clientX - r.left) / r.width - 0.5) * 14}px, ${((e.clientY - r.top) / r.height - 0.5) * 10}px)`;
  };

  if (reduced) return <StaticJourney onEnquire={onEnquire} />;

  const darkText = 'text-white';
  const J = JOURNEY;

  return (
    <section id="overview" ref={outer} data-testid="immersive-journey" className="relative h-[540vh] bg-[#14151A] sm:h-[620vh]" aria-label="Immersive journey — Life in a Metro One">
      <div ref={scene} className="sticky top-0 h-[100dvh] overflow-hidden">
        {/* 3D canvas — activates only when the approved GLB exists */}
        {glbActive && (
          <div ref={(el) => (R.canvas = el)} className="absolute inset-0 z-0" data-testid="journey-3d-canvas">
            <Suspense fallback={null}>
              <JourneyScene progressRef={progressRef} controlsRef={jm} onFirstFrame={() => setGlbLive(true)} />
            </Suspense>
          </div>
        )}

        {/* overall progress hairline */}
        <div className="absolute inset-x-0 top-0 z-40 h-[3px] bg-white/10">
          <div ref={(el) => (R.pbar = el)} className="h-full origin-left bg-[#C92F4B]" style={{ transform: 'scaleX(0)' }} />
        </div>

        {/* chapter label */}
        <div className="absolute left-4 top-20 z-40 sm:left-8" data-testid="journey-chapter-label">
          <p className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
            {String(chapter + 1).padStart(2, '0')} — {CHAPTERS[chapter].label}
          </p>
        </div>

        {/* skip intro — never trap the visitor in the scroll sequence */}
        {showSkip && (
          <button
            data-testid="skip-intro-btn"
            onClick={() => {
              track('journey_skip', { chapter: CHAPTERS[chapter].id });
              scrollToTarget(outer.current.offsetTop + outer.current.offsetHeight - window.innerHeight + 4);
            }}
            className="absolute bottom-[calc(5.9rem+env(safe-area-inset-bottom))] left-4 z-40 flex min-h-[44px] items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 font-display text-[10px] font-bold uppercase tracking-[0.22em] text-white backdrop-blur-md transition-colors hover:bg-white/20 sm:bottom-8 sm:left-8"
          >
            Skip Intro
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </button>
        )}

        {/* chapter rail (desktop) */}
        <div className="absolute right-6 top-1/2 z-40 hidden -translate-y-1/2 flex-col gap-4 lg:flex" data-testid="journey-chapter-rail">
          {CHAPTERS.map((c, i) => (
            <div key={c.label} className="flex items-center justify-end gap-2.5">
              <span className={`font-display text-[9px] font-bold uppercase tracking-[0.22em] transition-colors ${chapter === i ? 'text-[#FF5C73]' : 'text-white/30'}`}>
                {String(i + 1).padStart(2, '0')} · {c.label}
              </span>
              <span className="h-[3px] w-8 overflow-hidden rounded-full bg-white/15">
                <span ref={(el) => (R[`chf${i}`] = el)} className="block h-full origin-left bg-[#C92F4B]" style={{ transform: 'scaleX(0)' }} />
              </span>
            </div>
          ))}
        </div>

        {/* cursor ring */}
        <div ref={(el) => (R.cursor = el)} className="pointer-events-none absolute left-0 top-0 z-40 hidden h-9 w-9 rounded-full border border-[#C92F4B]/70 lg:block" aria-hidden="true" />

        {/* ── SCENE 0 · CINEMATIC INTRODUCTION ── */}
        <div ref={(el) => (R.c0 = el)} className={`absolute inset-0 z-10 ${glbLive ? '' : 'bg-[#14151A]'}`} style={{ visibility: 'hidden' }}>
          <div ref={(el) => (R.s0img = el)} role="presentation" aria-hidden="true"
            className={`absolute inset-x-0 bottom-0 mx-auto h-[46%] w-[64%] bg-contain bg-bottom bg-no-repeat ${glbLive ? 'invisible' : ''}`}
            style={{ opacity: 0.25, backgroundImage: 'url(/assets/silhouette.webp)' }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <div className="relative mb-8 flex items-center justify-center">
              <svg width="120" height="120" viewBox="0 0 120 120" className="absolute" aria-hidden="true">
                <circle cx="60" cy="60" r="48" fill="none" stroke="#C92F4B" strokeWidth="3" strokeLinecap="round" strokeDasharray="302" strokeDashoffset="302"
                  style={{ animation: 'dash-draw 1.1s ease-in-out 0.1s forwards' }} />
              </svg>
              <img src={BRAND.logo} alt="Codename Metro One — Navade" className="fade-auto w-36 rounded-md bg-white/95 p-1.5" style={{ animationDelay: '1s' }} />
            </div>
            <h1 className={`font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-5xl ${darkText}`}>
              <span className="block overflow-hidden"><span className="reveal-auto block" style={{ animationDelay: '0.15s' }}>{J.intro.lines[0]}</span></span>
              <span className="block overflow-hidden"><span className="reveal-auto block" style={{ animationDelay: '0.35s' }}>IS ALREADY <span className="text-[#FF5C73]">READY.</span></span></span>
            </h1>
            <div className="mt-10 flex flex-col items-center gap-2 text-white/50">
              <span className="fade-auto font-display text-[10px] font-bold uppercase tracking-[0.34em]" style={{ animationDelay: '0.8s' }}>{J.intro.cue}</span>
              <ChevronDown size={16} className="animate-bounce" aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* ── SCENE 1 · APPROACH ── */}
        <div ref={(el) => (R.c1 = el)} className={`absolute inset-0 z-10 ${glbLive ? '' : 'bg-[#16171B]'}`} style={{ visibility: 'hidden' }}>
          <picture>
            <source media="(max-width: 768px)" srcSet="/assets/hero-elevation-m.webp" />
            <img ref={(el) => (R.s1img = el)} src="/assets/hero-elevation.webp" alt="Aarohi Complex elevation at dusk" aria-hidden={glbOk}
              className={`absolute inset-x-0 bottom-0 mx-auto h-[70%] w-full object-cover object-bottom ${glbLive ? 'invisible' : ''}`}
              style={{ transformOrigin: 'center bottom', filter: 'brightness(0.28)' }} />
          </picture>
          <div ref={(el) => (R.s1fog = el)} className="absolute inset-0 bg-[radial-gradient(90%_70%_at_50%_80%,rgba(22,23,27,0.2),rgba(22,23,27,0.95))]" style={{ opacity: 0.9 }} />
          <div ref={(el) => (R.s1trail1 = el)} className="absolute left-0 top-[30%] h-[2px] w-[38%] bg-gradient-to-r from-transparent via-[#C92F4B] to-transparent blur-[1px]" style={{ transform: 'translateX(-50%)' }} aria-hidden="true" />
          <div ref={(el) => (R.s1trail2 = el)} className="absolute right-0 top-[64%] h-[2px] w-[30%] bg-gradient-to-r from-transparent via-[#A7ABB0] to-transparent blur-[1px]" style={{ transform: 'translateX(60%)' }} aria-hidden="true" />
          <div className="absolute inset-0 flex items-end pb-[16dvh] sm:items-center sm:pb-0">
            <div className="px-6 sm:px-14">
              <h2 className={`font-display font-extrabold uppercase leading-[0.98] tracking-tight ${darkText} text-4xl sm:text-7xl lg:text-8xl`}>
                <Line t={0.25}>{J.approach.lines[0]}</Line>
                <Line t={0.42}><span className="text-[#C92F4B] drop-shadow-[0_6px_30px_rgba(201,47,75,0.45)]">METRO ONE</span></Line>
              </h2>
            </div>
          </div>
        </div>

        {/* ── SCENE 2 · READY HOME REVEAL ── */}
        <div ref={(el) => (R.c2 = el)} className={`absolute inset-0 z-10 ${glbLive ? '' : 'bg-[#0B0E14]'}`} style={{ visibility: 'hidden' }}>
          <picture>
            <source media="(max-width: 768px)" srcSet="/assets/elevation-day-m.webp" />
            <img loading="lazy" ref={(el) => (R.s2img = el)} src="/assets/elevation-day.webp" alt="Low-angle view of the ready Aarohi Complex façade" aria-hidden={glbOk}
              className={`absolute inset-0 h-full w-full object-cover ${glbLive ? 'invisible' : ''}`}
              style={{ objectPosition: 'center 72%', transform: 'translateY(13%) scale(1.32)', filter: 'brightness(0.35)' }} />
          </picture>
          <div ref={(el) => (R.s2night = el)} className="absolute inset-0 bg-[#0B0E14]" style={{ opacity: 0.88 }} />
          <div ref={(el) => (R.s2blue = el)} className="absolute inset-0 bg-[#31527B] mix-blend-overlay" style={{ opacity: 0.55 }} />
          <div className="absolute inset-0 flex items-end bg-[linear-gradient(to_top,rgba(11,14,20,0.72),transparent_55%)] pb-[12dvh]">
            <div className="px-6 sm:px-14">
              <h2 className={`font-display font-extrabold uppercase leading-tight tracking-tight ${darkText} text-3xl sm:text-6xl`}>
                <Line t={0.12}>{J.ready.steps[0]}</Line>
                <Line t={0.34} className="text-xl text-[#FF9DAD] sm:text-3xl">{J.ready.steps[1]}</Line>
                <Line t={0.56} className="text-xl sm:text-3xl">OC RECEIVED</Line>
              </h2>
              <Line t={0.74} className="mt-4 max-w-md text-base text-white/75">{J.ready.support}</Line>
            </div>
          </div>
        </div>

        {/* ── SCENE 3 · ARCHITECTURAL ORBIT ── */}
        <div ref={(el) => (R.c3 = el)} className={`absolute inset-0 z-10 ${glbLive ? '' : 'bg-[#101115]'}`} style={{ visibility: 'hidden' }}>
          {!glbLive && (
            <>
              <picture>
                <source media="(max-width: 768px)" srcSet="/assets/hero-elevation-m.webp" />
                <img loading="lazy" ref={(el) => (R.s3imgA = el)} src="/assets/hero-elevation.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" style={{ transformOrigin: 'center 60%' }} />
              </picture>
              <picture>
                <source media="(max-width: 768px)" srcSet="/assets/elevation-day-m.webp" />
                <img loading="lazy" ref={(el) => (R.s3imgB = el)} src="/assets/elevation-day.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: 0, transformOrigin: 'center 60%' }} />
              </picture>
            </>
          )}
          <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(16,17,21,0.8),transparent_45%)]" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            {J.orbit.words.map((w, i) => (
              <span key={w} data-reveal data-word data-t={0.08 + i * 0.24} data-o={0.3 + i * 0.24} data-d={0.14}
                className="absolute font-display text-5xl font-black uppercase tracking-tight text-white sm:text-8xl"
                style={{ opacity: 0 }}>
                {w === 'CONNECTED' ? <>C<span className="text-[#C92F4B]">O</span>NNECTED</> : w}
              </span>
            ))}
            <span data-reveal data-t={0.82} className="mt-56 font-display text-xs font-bold uppercase tracking-[0.3em] text-white/70 sm:text-sm" style={{ opacity: 0 }}>
              {J.orbit.sub}
            </span>
          </div>
        </div>

        {/* ── SCENE 4 · EXPERIENCE THE HOME (portal) ── */}
        <div ref={(el) => (R.c4 = el)} className="absolute inset-0 z-10 bg-[#14151A]" style={{ visibility: 'hidden' }}>
          <div ref={(el) => (R.s4wrap = el)} className="absolute inset-0 bg-[#FAF9F6]" style={{ opacity: 0 }}>
            {['/assets/room-living.webp', '/assets/room-kitchen.webp', '/assets/room-bedroom.webp'].map((src, i) => (
              <picture key={src}>
                <source media="(max-width: 768px)" data-srcset={src.replace('.webp', '-m.webp')} className="jdefer-src" />
                <img loading="lazy" ref={(el) => (R[`s4i${i}`] = el)} data-src={src} alt={['Approved living room', 'Approved kitchen', 'Approved bedroom'][i] + ' at Aarohi Complex'}
                  className="jdefer absolute inset-0 h-full w-full object-cover" style={{ opacity: 0 }} />
              </picture>
            ))}
            <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(250,249,246,0.96)_8%,transparent_55%)]" />
            <div className="absolute inset-x-0 bottom-0 px-6 pb-[10dvh] sm:px-14">
              <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-[#1C1D21] sm:text-6xl">
                <Line t={0.42}>YOUR H<span className="o-red">O</span>ME. YOUR WAY.</Line>
              </h2>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {J.homes.options.map((o, i) => (
                  <span key={o} data-reveal data-t={0.55 + i * 0.1} className="rounded-full border border-[#C92F4B]/40 bg-white px-5 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.16em] text-[#A41225]" style={{ opacity: 0 }}>
                    {o}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div ref={(el) => (R.s4ring = el)} className="absolute left-1/2 top-1/2 h-28 w-28 rounded-full border-[7px] border-[#C92F4B]" style={{ transform: 'translate(-50%,-50%) scale(0.25)' }} aria-hidden="true" />
        </div>

        {/* ── SCENE 5 · SPACE TO BREATHE ── */}
        <div ref={(el) => (R.c5 = el)} className="absolute inset-0 z-10 bg-[#FAF9F6]" style={{ visibility: 'hidden' }}>
          <picture>
            <source media="(max-width: 768px)" srcSet="/assets/balcony-xl-m.webp" />
            <img loading="lazy" ref={(el) => (R.s5l0 = el)} src="/assets/balcony-xl.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-40 blur-[2px]" style={{ transform: 'scale(1.22)' }} />
          </picture>
          <picture>
            <source media="(max-width: 768px)" srcSet="/assets/balcony-xl-m.webp" />
            <img loading="lazy" ref={(el) => (R.s5l1 = el)} src="/assets/balcony-xl.webp" alt="Extra-large ventilated balcony at Aarohi Complex" className="absolute inset-0 h-full w-full object-cover" style={{ transform: 'scale(1.1)' }} />
          </picture>
          <div ref={(el) => (R.s5light = el)} className="absolute inset-0 bg-[radial-gradient(70%_55%_at_25%_12%,rgba(245,158,11,0.35),transparent_65%)]" style={{ opacity: 0.12 }} />
          <div className="absolute inset-0 flex items-end bg-[linear-gradient(to_top,rgba(28,29,33,0.6),transparent_50%)] pb-[12dvh]">
            <div className="px-6 sm:px-14">
              <h2 className={`font-display text-3xl font-extrabold uppercase tracking-tight sm:text-6xl ${darkText}`}>
                <Line t={0.25}>SPACE T<span className="text-[#FF5C73]">O</span> BREATHE.</Line>
              </h2>
              <Line t={0.45} className="mt-3 max-w-md text-base text-white/85">{J.breathe.support}</Line>
            </div>
          </div>
        </div>

        {/* ── SCENE 6 · CONNECTED LIFESTYLE ── */}
        <div ref={(el) => (R.c6 = el)} className="absolute inset-0 z-10 bg-[#FAF9F6]" style={{ visibility: 'hidden' }}>
          <picture>
            <source media="(max-width: 768px)" srcSet="/assets/map-connectivity-m.webp" />
            <img loading="lazy" ref={(el) => (R.s6map = el)} src="/assets/map-connectivity.webp" alt="Approved Navade connectivity map" className="absolute inset-0 h-full w-full object-cover"
              style={{ transform: 'translateY(8%) scale(1.22)', filter: 'brightness(0.82)' }} />
          </picture>
          <div ref={(el) => (R.s6dots = el)} className="absolute inset-0" style={{ opacity: 0 }} aria-hidden="true">
            <span className="pulse-dot absolute left-[52%] top-[46%] h-3 w-3 bg-[#C92F4B]" />
            <span className="pulse-dot absolute left-[38%] top-[58%] h-3 w-3 bg-[#15803D]" style={{ animationDelay: '0.5s' }} />
            <span className="pulse-dot absolute left-[66%] top-[60%] h-3 w-3 bg-[#A7ABB0]" style={{ animationDelay: '1s' }} />
          </div>
          <div className="absolute inset-0 flex flex-col justify-between bg-[linear-gradient(to_bottom,rgba(250,249,246,0.9),transparent_35%,transparent_60%,rgba(250,249,246,0.94))] px-6 pb-[9dvh] pt-24 sm:px-14">
            <h2 className="max-w-xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-[#1C1D21] sm:text-5xl">
              <Line t={0.2}>C<span className="o-red">O</span>NNECTED T<span className="o-red">O</span> WHAT M<span className="o-red">O</span>VES Y<span className="o-red">O</span>U.</Line>
            </h2>
            <div className="flex flex-wrap gap-2.5">
              <span data-reveal data-t={0.45} className="rounded-full bg-[#15803D]/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#15803D]" style={{ opacity: 0 }}>Rail & Road — {J.connected.operational}</span>
              <span data-reveal data-t={0.55} className="rounded-full bg-[#404145]/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#404145]" style={{ opacity: 0 }}>Metro & Air — {J.connected.proposed}</span>
            </div>
          </div>
        </div>

        {/* ── SCENE 7 · FINAL REVEAL ── */}
        <div ref={(el) => (R.c7 = el)} className={`absolute inset-0 z-10 ${glbLive ? '' : 'bg-[#FAF9F6]'}`} style={{ visibility: 'hidden' }}>
          <picture>
            <source media="(max-width: 768px)" srcSet="/assets/elevation-day-m.webp" />
            <img loading="lazy" ref={(el) => (R.s7img = el)} src="/assets/elevation-day.webp" alt="Complete daylight elevation of Aarohi Complex, Codename Metro One Navade" aria-hidden={glbOk}
              className={`absolute inset-0 h-full w-full object-cover ${glbLive ? 'invisible' : ''}`}
              style={{ objectPosition: 'center 40%', transform: 'translateY(7%) scale(1.14)', filter: 'brightness(0.95)' }} />
          </picture>
          <div className={`absolute inset-0 ${glbLive ? 'bg-[linear-gradient(to_top,rgba(250,249,246,0.97)_14%,rgba(250,249,246,0.8)_44%,transparent_62%)] sm:bg-[linear-gradient(to_right,rgba(250,249,246,0.94)_0%,rgba(250,249,246,0.7)_36%,transparent_56%)]' : 'bg-[linear-gradient(to_top,rgba(250,249,246,0.99)_16%,rgba(250,249,246,0.85)_45%,rgba(250,249,246,0.35)_65%,transparent_78%)]'}`} />
          <div className="absolute inset-x-0 bottom-0 px-6 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:px-14 md:pb-10">
            <p data-reveal data-t={0.4} className="inline-block rounded-full bg-[#C92F4B] px-4 py-1.5 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-white" style={{ opacity: 0 }}>{J.final.badge}</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-tight text-[#1C1D21] sm:text-6xl">
              <Line t={0.12}>LIFE IN A METR<span className="o-red">O</span> <span className="o-red">O</span>NE</Line>
            </h2>
            <Line t={0.28} className="mt-2 font-display text-xs font-bold uppercase tracking-[0.22em] text-[#A41225] sm:text-sm">{J.final.sub}</Line>
            <Line t={0.5} className="mt-2 max-w-md text-base text-[#404145]">{J.final.note}</Line>
            <div ref={(el) => (R.s7cta = el)} data-reveal data-t={0.66} className="mt-6" style={{ opacity: 0, pointerEvents: 'none' }}>
              <p className="font-display text-[11px] font-bold uppercase tracking-[0.24em] text-[#404145]">Call or WhatsApp: <span className="text-[#A41225]">{CONTACT.phoneDisplay}</span></p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <button data-testid="journey-visit-btn" onClick={() => { track('book_visit', { placement: 'journey_final' }); onEnquire(); }} onPointerMove={magnet} onPointerLeave={magnet} className="btn-primary !px-6 !py-3 !text-xs transition-transform">
                  <CalendarCheck size={15} /> Schedule a Visit
                </button>
                <a data-testid="journey-whatsapp-btn" href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent('Hi, I want to book a private site visit for Metro One, Navade.')}`} target="_blank" rel="noopener noreferrer" onClick={() => track('whatsapp_click', { placement: 'journey_final' })} onPointerMove={magnet} onPointerLeave={magnet} className="btn-primary !bg-[#128C4B] !px-6 !py-3 !text-xs transition-transform hover:!bg-[#0E6E3B]">
                  <MessageCircle size={15} /> WhatsApp Now
                </a>
                <a data-testid="journey-call-btn" href={`tel:${CONTACT.phone}`} onClick={() => track('call_click', { placement: 'journey_final' })} onPointerMove={magnet} onPointerLeave={magnet} className="btn-ghost !px-6 !py-3 !text-xs transition-transform">
                  <Phone size={15} /> Call Now
                </a>
                <button data-testid="journey-brochure-btn" onClick={() => { track('brochure_download_intent', { placement: 'journey_final' }); onEnquire({ brochure: true }); }} onPointerMove={magnet} onPointerLeave={magnet} className="btn-ghost !px-6 !py-3 !text-xs transition-transform">
                  <Download size={15} /> Brochure
                </button>
              </div>
            </div>
            <div data-reveal data-t={0.82} className="mt-5 flex flex-wrap gap-2" style={{ opacity: 0 }}>
              {['Ready to Move In', 'G+7 Development', '1 & 2 BHK Homes', `MahaRERA: ${BRAND.rera}`].map((t) => (
                <span key={t} className="chip !bg-white/90">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* loading overlay */}
        {!ready && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#14151A]" data-testid="journey-loader">
            <svg width="84" height="84" viewBox="0 0 84 84" aria-hidden="true">
              <circle cx="42" cy="42" r="34" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
              <circle cx="42" cy="42" r="34" fill="none" stroke="#C92F4B" strokeWidth="3" strokeLinecap="round" strokeDasharray="214" strokeDashoffset={214 - (214 * loadPct) / 100} className="-rotate-90" style={{ transformOrigin: 'center' }} />
            </svg>
            <p className="mt-4 font-display text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">{loadPct}%</p>
          </div>
        )}
      </div>
    </section>
  );
}
